import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSelector from './LanguageSelector';
import {
  Activity,
  Lock,
  User,
  ArrowRight,
  AlertCircle,
  Fingerprint,
  Info,
  UserPlus,
  ArrowLeft,
  CheckCircle2,
  CreditCard,
  QrCode,
  ShieldCheck,
  Star,
  Smartphone,
  Copy,
  Check,
  Building2,
  Sparkles,
  CreditCard as CardIcon,
  Server,
  Wifi,
  Stethoscope,
  MapPin,
  Mail,
  Phone,
  KeyRound,
  Send
} from 'lucide-react';

// ✅ Firebase
import { auth, googleProvider, appleProvider, db } from '../firebase/client';
import { activateTrial } from '../services/trialService';
import {
  signInWithEmailAndPassword,
  signInWithPopup,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail, sendEmailVerification, signOut,
  linkWithCredential, fetchSignInMethodsForEmail,
} from 'firebase/auth';
import {
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
} from 'firebase/firestore';
import {
  buildInitialUsageState,
  getPlanUsageLimits,
} from '../services/usageLimits';
import {
  buildInactivePlan,
  buildPlanPatch,
  readPlanId,
  readPlanStatus,
} from '../services/plan';
import {
  ORTHO_SPECIALTIES,
} from '../utils/doctorProfile';
import {
  clearVerificationFlowInProgress,
  setVerificationFlowInProgress,
} from '../utils/verificationFlow';

interface LoginScreenProps {
  onOpenSubscribe?: (params: {
    plan: 'pro' | 'premium';
    method: 'credit_card' | 'pix';
    priceId: string;
    successUrl: string;
    cancelUrl: string;
  }) => Promise<void>;
}

type ViewState = 'login' | 'register' | 'payment' | 'forgot_password' | 'trial';
type PaymentMethod = 'credit_card' | 'pix';

interface AdminPaymentConfig {
  pixKey: string;
  pixBeneficiary: string;
  pixBank: string;
  pixQrCode: string;
  ccBank: string;
}

// 🔒 Convenção simples de assinatura no Firestore (você pode ajustar depois)
// users/{uid} {
//   role: 'subscriber' | 'admin'
//   subscriptionStatus: 'paid' | 'pending' | 'expired'
//   plan: 'monthly' | 'yearly'
//   paymentDate: number | null
//   ...campos ortho id
// }

const MS_PER_DAY = 86400000;
const VERIFICATION_EMAIL_STORAGE_KEY = 'orthoprecision_verification_email';
const BLOCKED_EMAIL_DOMAINS = new Set([
  'mailinator.com',
  'tempmail.com',
  '10minutemail.com',
  'guerrillamail.com',
  'yopmail.com',
]);

const LoginScreen: React.FC<LoginScreenProps> = ({ onOpenSubscribe }) => {
  const { t } = useLanguage();

  // View State management
  const [currentView, setCurrentView] = useState<ViewState>('login');

  // Form States
  const [username, setUsername] = useState(''); // aqui será o EMAIL (recomendado)
  const [password, setPassword] = useState('');

  // Registration specific states
  const [regName, setRegName] = useState('');
  const [regUsername, setRegUsername] = useState(''); // pode ser "ID interno" (opcional)
  const [regPassword, setRegPassword] = useState('');
  const [regConfirmPassword, setRegConfirmPassword] = useState('');

  // Ortho ID Fields
  const [regCRM, setRegCRM] = useState('');
  const [regRQE, setRegRQE] = useState('');
  const [regSpecialty, setRegSpecialty] = useState<string>(ORTHO_SPECIALTIES[2]);
  const [regSubspecialty, setRegSubspecialty] = useState('');
  const [regGender, setRegGender] = useState('');
  const [regHospital, setRegHospital] = useState('');
  const [regPhone, setRegPhone] = useState('');
  const [regEmail, setRegEmail] = useState(''); // EMAIL real (vamos usar este no Firebase)

  const [regSuccess, setRegSuccess] = useState(false);

  // Forgot Password States
  const [resetEmail, setResetEmail] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);

  // Trial activation states (email/password path)
  const [trialAuthMode, setTrialAuthMode] = useState<'options' | 'signin' | 'signup'>('options');
  const [trialEmail, setTrialEmail] = useState('');
  const [trialPassword, setTrialPassword] = useState('');
  const [trialConfirmPassword, setTrialConfirmPassword] = useState('');

  // Payment specific states
  const [pendingUserUid, setPendingUserUid] = useState<string | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<'pro' | 'premium'>('pro');
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('credit_card');
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [processingStage, setProcessingStage] = useState('');
  const [adminConfig, setAdminConfig] = useState<AdminPaymentConfig | null>(null);
  const [copiedKey, setCopiedKey] = useState(false);

  const [postRegisterAction, setPostRegisterAction] = useState<null | "payment">(null);
  const [error, setError] = useState('');
  const [infoMessage, setInfoMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [verificationEmailTarget, setVerificationEmailTarget] = useState('');
  const [isResendingVerification, setIsResendingVerification] = useState(false);
  const [showVerificationActions, setShowVerificationActions] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  React.useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = window.setTimeout(() => setResendCooldown((seconds) => Math.max(0, seconds - 1)), 1000);
    return () => window.clearTimeout(timer);
  }, [resendCooldown]);

// ✅ COLE AQUI (antes do useEffect)
  const specialtyOptions = [
    { value: ORTHO_SPECIALTIES[0], label: t('specialtyOrthopedist') },
    { value: ORTHO_SPECIALTIES[1], label: t('specialtyTraumatologist') },
    { value: ORTHO_SPECIALTIES[2], label: t('specialtyDoctor') },
  ] as const;

  const subspecialtyOptions = [
    t('subspecialtyHand'),
    t('subspecialtySpine'),
    t('subspecialtyKnee'),
    t('subspecialtyShoulderElbow'),
    t('subspecialtyHip'),
    t('subspecialtyPediatric'),
    t('subspecialtyTrauma'),
    t('subspecialtySportsMedicine'),
  ];

  const PLAN_CONFIG = {
    pro: {
      label: t('planProLabel'),
      priceLabel: t('planProPrice'),
      subscriptionPriceId: "price_1Tg6jL0q8UQtG4iTndtSq2N4",
      pixPriceId: "",
    },
    premium: {
      label: t('planPremiumLabel'),
      priceLabel: t('planPremiumPrice'),
      subscriptionPriceId: "price_1Tg6jJ0q8UQtG4iTANeOirCO",
      pixPriceId: "",
    },
  } as const;

  const selectedPlanConfig = PLAN_CONFIG[selectedPlan];

  // ✅ Se você ainda usa config de pix no localStorage (mantive compatível)
  React.useEffect(() => {
    const savedConfig = localStorage.getItem('orthoflow_admin_payment');
    if (savedConfig) {
      try {
        const parsed = JSON.parse(savedConfig);
        setAdminConfig({
          pixKey: parsed.pixKey || '',
          pixBeneficiary: parsed.pixBeneficiary || '',
          pixBank: parsed.pixBank || '',
          pixQrCode: parsed.pixQrCode || '',
          ccBank: parsed.ccBank || ''
        });
      } catch (e) {
        console.error(e);
      }
    }
  }, []);

  React.useEffect(() => {
    if (currentView !== 'login') return;

    const authNotice = localStorage.getItem('orthoprecision_auth_notice');
    const storedVerificationEmail = localStorage.getItem(VERIFICATION_EMAIL_STORAGE_KEY) || '';
    if (authNotice) {
      setInfoMessage(authNotice);
      localStorage.removeItem('orthoprecision_auth_notice');
    }
    if (storedVerificationEmail) {
      setVerificationEmailTarget(storedVerificationEmail);
      if (!username) {
        setUsername(storedVerificationEmail);
      }
    }
  }, [currentView]);

  React.useEffect(() => {
    if (currentView !== 'register') return;
    setRegSpecialty(ORTHO_SPECIALTIES[2]);
  }, [currentView]);

  const startRegistration = () => {
  setPostRegisterAction(null);
  setPendingUserUid(null);
  setError("");
  setRegSuccess(false);
  setShowVerificationActions(false);
  setRegSpecialty(ORTHO_SPECIALTIES[2]);
  setRegSubspecialty('');
  setCurrentView("register");
};

  const getUserDoc = async (uid: string) => {
    const ref = doc(db, 'users', uid);
    const snap = await getDoc(ref);
    return snap.exists() ? snap.data() : null;
  };

  const getEmailDomain = (email: string) =>
    email.trim().toLowerCase().split('@')[1] || '';

  const isDisposableEmailDomain = (email: string) => {
    const domain = getEmailDomain(email);
    if (!domain) return false;

    return Array.from(BLOCKED_EMAIL_DOMAINS).some(
      (blockedDomain) =>
        domain === blockedDomain || domain.endsWith(`.${blockedDomain}`)
    );
  };

  const requiresVerifiedPasswordLogin = (user: { emailVerified?: boolean; providerData?: Array<{ providerId?: string | null }> } | null) =>
    !!user?.providerData?.some((provider) => provider.providerId === 'password') &&
    !user?.emailVerified;

  const persistVerificationEmailTarget = (email: string) => {
    const normalizedEmail = email.trim();
    setVerificationEmailTarget(normalizedEmail);

    if (normalizedEmail) {
      localStorage.setItem(VERIFICATION_EMAIL_STORAGE_KEY, normalizedEmail);
    } else {
      localStorage.removeItem(VERIFICATION_EMAIL_STORAGE_KEY);
    }
  };

  const getFirebaseErrorDetails = (err: any, fallback: string) => {
    const rawMessage =
      typeof err?.message === 'string' && err.message.trim()
        ? err.message.trim()
        : '';
    const rawCode =
      typeof err?.code === 'string' && err.code.trim()
        ? err.code.trim()
        : '';

    if (rawMessage) {
      return `${fallback} (${rawMessage})`;
    }

    if (rawCode) {
      return `${fallback} (${rawCode})`;
    }

    return fallback;
  };

  const sendVerificationEmailWithLogging = async (
    user: { uid?: string; email?: string | null } | null,
    email: string,
    context: 'register' | 'resend'
  ) => {
    console.info(`[auth] ${context}: sending verification email`, {
      uid: user?.uid || null,
      email,
    });

    if (!user) {
      console.error(
        `[auth] ${context}: no authenticated user available for verification email`,
        { email }
      );

      return {
        ok: false as const,
        message:
          t('loginVerificationSessionClosed'),
      };
    }

    try {
      await sendEmailVerification(user as any);
      console.info(`[auth] ${context}: verification email sent successfully`, {
        uid: user?.uid || null,
        email,
      });
      persistVerificationEmailTarget(email);
      return { ok: true as const };
    } catch (err: any) {
      console.error(`[auth] ${context}: failed to send verification email`, {
        uid: user?.uid || null,
        email,
        code: err?.code || null,
        message: err?.message || null,
      });

      return {
        ok: false as const,
        message: getFirebaseErrorDetails(
          err,
          t('loginVerificationEmailFailed')
        ),
      };
    }
  };

  const createDefaultGoogleUserDoc = async (uid: string, user: { displayName?: string | null; email?: string | null }) => {
    const email = user.email?.trim() || '';
    const name = user.displayName?.trim() || (email ? email.split('@')[0] : '');
    const plan = buildInactivePlan();

    const userDoc = {
      role: 'free',
      tier: 'free',
      planTier: 'free',
      subscriptionTier: 'free',
      subscriptionStatus: plan.status,
      isPremium: false,
      paymentDate: null,
      displayName: name,
      accessLevel: 'free',
      name,
      username: email || uid,
      email,
      crm: '',
      rqe: '',
      specialty: ORTHO_SPECIALTIES[2],
      subspecialty: '',
      gender: '',
      hospital: '',
      phone: '',
      professionalVerification: {
        status: 'not_submitted',
        method: 'none',
        crm: '',
        crmState: '',
        rqe: '',
        specialty: ORTHO_SPECIALTIES[2],
        subspecialty: '',
        message: '',
      },
      ...buildPlanPatch(plan),
      limits: getPlanUsageLimits('free'),
      usage: buildInitialUsageState(),
      profile: {
        name,
        email,
        crm: '',
        rqe: '',
        gender: '',
        crmState: '',
        specialty: ORTHO_SPECIALTIES[2],
        subspecialty: '',
        hospital: '',
        phone: '',
      },
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    await setDoc(doc(db, 'users', uid), userDoc, { merge: true });
    return userDoc;
  };

  const canAccess = (userData: any): { ok: boolean; reason?: string } => {
  // Se não existir doc ainda, deixa entrar
  if (!userData) return { ok: true };

  // Admin sempre entra
  if (userData.role === "admin") return { ok: true };

  const tier = readPlanId(userData);
  const status = readPlanStatus(userData);
  // FREE entra no app com acesso limitado
  if (tier === "free") {
    return { ok: true };
  }

  // PLANOS PAGOS entram no app
  if (tier === "pro" || tier === "premium") {
    if (status === "active") {
      return { ok: true };
    }

    // Mesmo se estiver inconsistente, deixa entrar e o bloqueio fica por módulo/plano
    return { ok: true };
  }

  return { ok: true };
};

// ✅ FORGOT PASSWORD (Firebase)
const handleResetRequest = async (e: React.FormEvent) => {
  e.preventDefault();
  setInfoMessage('');

  if (!resetEmail) {
      setError(t('loginEnterEmail'));
    return;
  }

  setIsLoading(true);
  setError('');

  try {
    await sendPasswordResetEmail(auth, resetEmail.trim());
    setResetSuccess(true);
  } catch (err) {
    console.error(err);
      setError(t('loginResetEmailFailed'));
  } finally {
    setIsLoading(false);
    setPendingUserUid(null);
    setPostRegisterAction(null);
    setRegSuccess(false);
    setCurrentView("login");
    return;
  }
};

    // ✅ LOGIN Email/Senha (Firebase) — sem onLogin (AuthContext cuida do resto)
  const handleLogin = async (e: React.FormEvent) => {
  e.preventDefault();
  setIsLoading(true);
  setError('');
  setInfoMessage('');

  try {
    const email = username.trim();

    if (!email) {
      setError(t('loginEnterSurgeonId'));
      setIsLoading(false);
      return;
    }

    if (!password) {
      setError(t('loginEnterPassword'));
      setIsLoading(false);
      return;
    }

    const cred = await signInWithEmailAndPassword(auth, email, password);

    await cred.user.reload();

    if (requiresVerifiedPasswordLogin(auth.currentUser)) {
      console.info('[auth] login blocked: email not verified', {
        uid: cred.user.uid,
        email,
      });
      persistVerificationEmailTarget(email);
      setShowVerificationActions(true);
      setError(t('emailVerificationRequired'));
      setIsLoading(false);
      return;
    }

    const userData = await getUserDoc(cred.user.uid);
    const access = canAccess(userData);

    if (!access.ok) {
      setError(access.reason || t('loginRestrictedAccess'));
      setIsLoading(false);
      return;
    }

    setPendingUserUid(null);
    setPostRegisterAction(null);
    persistVerificationEmailTarget('');
    setShowVerificationActions(false);
    setCurrentView('login');
    setIsLoading(false);
  } catch (err: any) {
    console.error(err);
    setError(t('loginAccessDenied'));
    setIsLoading(false);
  }
};
    // ✅ LOGIN Google (Firebase) — você disse que já abre, aqui fica completo
    // ✅ LOGIN Google (Firebase) — sem onLogin
  const handleGoogleLogin = async () => {
  setIsLoading(true);
  setError('');
  setInfoMessage('');
  setShowVerificationActions(false);

  try {
    const cred = await signInWithPopup(auth, googleProvider);

    let userData = await getUserDoc(cred.user.uid);

    if (!userData) {
      userData = await createDefaultGoogleUserDoc(cred.user.uid, {
        displayName: cred.user.displayName,
        email: cred.user.email,
      });
    }

    const access = canAccess(userData);

    if (!access.ok) {
      setError(access.reason || t('loginRestrictedAccess'));
      setIsLoading(false);
      return;
    }

    setPendingUserUid(null);
    setPostRegisterAction(null);
    setCurrentView('login');
    setShowVerificationActions(false);
  } catch (err) {
    console.error(err);
    setError(t('loginGoogleSignInError'));
  } finally {
    setIsLoading(false);
  }
};

  const handleAppleLogin = async () => {
    setIsLoading(true);
    setError('');
    setInfoMessage('');
    setShowVerificationActions(false);

    try {
      const cred = await signInWithPopup(auth, appleProvider);
      const appleUser = cred.user;

      let userData = await getUserDoc(appleUser.uid);

      if (!userData) {
        userData = await createDefaultGoogleUserDoc(appleUser.uid, {
          displayName: appleUser.displayName,
          email: appleUser.email,
        });
      }

      const access = canAccess(userData);
      if (!access.ok) {
        setError(access.reason || t('loginRestrictedAccess'));
        return;
      }

      setPendingUserUid(null);
      setPostRegisterAction(null);
      setCurrentView('login');
      setShowVerificationActions(false);
    } catch (err: any) {
      // Usuário fechou o popup — silencioso
      if (err?.code === 'auth/popup-closed-by-user' || err?.code === 'auth/cancelled-popup-request') {
        return;
      }
      // Conta já existe com outro provider — tentar vincular
      if (err?.code === 'auth/account-exists-with-different-credential') {
        const email = err?.customData?.email as string | undefined;
        if (email) {
          try {
            const methods = await fetchSignInMethodsForEmail(auth, email);
            const hint = methods[0] === 'google.com'
              ? 'Esse email já está vinculado a uma conta Google. Entre com Google primeiro.'
              : 'Esse email já está cadastrado. Entre com email e senha primeiro.';
            setError(hint);
          } catch {
            setError('Esse email já está associado a outra forma de login.');
          }
        } else {
          setError('Esse email já está associado a outra forma de login.');
        }
        return;
      }
      console.error('[apple login error]', err);
      setError('Não foi possível entrar com Apple. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  // ── Trial handlers ──────────────────────────────────────────────────────────

  const handleActivateTrial = async () => {
    try {
      await activateTrial();
      setInfoMessage('✅ Teste gratuito ativado! Aproveite 30 dias de acesso completo.');
    } catch (err: any) {
      const code = String(err?.code || err?.message || '');
      if (code.includes('already-exists')) {
        setInfoMessage('Você já utilizou o período de teste gratuito.');
      } else if (code.includes('resource-exhausted')) {
        setError('O limite de vagas do trial foi atingido.');
      } else if (code.includes('failed-precondition')) {
        setError('Trial não está disponível no momento. Tente mais tarde.');
      } else {
        setError('Não foi possível ativar o trial. Verifique se as funções estão publicadas e tente novamente.');
        console.error('[trial] activation error:', code, err);
      }
    }
  };

  const handleGoogleTrialLogin = async () => {
    setIsLoading(true);
    setError('');
    setInfoMessage('');
    try {
      const cred = await signInWithPopup(auth, googleProvider);
      if (!cred.user) return;
      let userData = await getUserDoc(cred.user.uid);
      if (!userData) {
        userData = await createDefaultGoogleUserDoc(cred.user.uid, {
          displayName: cred.user.displayName,
          email: cred.user.email,
        });
      }
      await handleActivateTrial();
    } catch (err: any) {
      if (err?.code === 'auth/popup-closed-by-user' || err?.code === 'auth/cancelled-popup-request') return;
      console.error('[trial google]', err);
      setError(t('loginGoogleSignInError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleAppleTrialLogin = async () => {
    setIsLoading(true);
    setError('');
    setInfoMessage('');
    try {
      const cred = await signInWithPopup(auth, appleProvider);
      if (!cred.user) return;
      let userData = await getUserDoc(cred.user.uid);
      if (!userData) {
        userData = await createDefaultGoogleUserDoc(cred.user.uid, {
          displayName: cred.user.displayName,
          email: cred.user.email,
        });
      }
      await handleActivateTrial();
    } catch (err: any) {
      if (err?.code === 'auth/popup-closed-by-user' || err?.code === 'auth/cancelled-popup-request') return;
      console.error('[trial apple]', err);
      setError('Não foi possível entrar com Apple. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const resetTrialAuthForm = () => {
    setTrialAuthMode('options');
    setTrialEmail('');
    setTrialPassword('');
    setTrialConfirmPassword('');
    setError('');
    setInfoMessage('');
  };

  const handleEmailTrialSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setInfoMessage('');

    try {
      const email = trialEmail.trim();

      if (!email) {
        setError(t('loginEnterSurgeonId'));
        setIsLoading(false);
        return;
      }

      if (!trialPassword) {
        setError(t('loginEnterPassword'));
        setIsLoading(false);
        return;
      }

      const cred = await signInWithEmailAndPassword(auth, email, trialPassword);
      await cred.user.reload();

      if (requiresVerifiedPasswordLogin(auth.currentUser)) {
        persistVerificationEmailTarget(email);
        setError(t('trialVerifyEmailFirst'));
        setIsLoading(false);
        return;
      }

      await handleActivateTrial();
    } catch (err: any) {
      console.error('[trial email signin]', err);
      setError(t('loginAccessDenied'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailTrialSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setInfoMessage('');

    try {
      const email = trialEmail.trim();

      if (!email || !email.includes('@')) {
        setError(t('registerInvalidEmail'));
        setIsLoading(false);
        return;
      }

      if (isDisposableEmailDomain(email)) {
        setError(t('disposableEmailBlockedError'));
        setIsLoading(false);
        return;
      }

      if (!trialPassword || trialPassword.length < 6) {
        setError(t('registerWeakPassword'));
        setIsLoading(false);
        return;
      }

      if (trialPassword !== trialConfirmPassword) {
        setError(t('registerPasswordsMismatch'));
        setIsLoading(false);
        return;
      }

      const cred = await createUserWithEmailAndPassword(auth, email, trialPassword);

      await createDefaultGoogleUserDoc(cred.user.uid, {
        displayName: email.split('@')[0],
        email,
      });

      // Envia o email de verificação em segundo plano — não bloqueia a ativação do trial.
      sendVerificationEmailWithLogging(cred.user, email, 'register').catch((verificationErr) => {
        console.error('[trial email signup] verification email failed', verificationErr);
      });

      await handleActivateTrial();
    } catch (err: any) {
      console.error('[trial email signup]', err);
      if (err?.code === 'auth/email-already-in-use') {
        setError(t('registerEmailAlreadyUsed'));
        setTrialAuthMode('signin');
      } else if (err?.code === 'auth/invalid-email') {
        setError(t('registerInvalidEmail'));
      } else if (err?.code === 'auth/weak-password') {
        setError(t('registerWeakPassword'));
      } else {
        setError(t('registerCreateUserFailed'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendVerificationEmail = async () => {
    if (resendCooldown > 0) return;

    const email = (username || verificationEmailTarget).trim();

    if (!email || !password) {
      setError(t('verificationResendCredentialsRequired'));
      return;
    }

    setIsResendingVerification(true);
    setError('');
    setInfoMessage('');

    try {
      setVerificationFlowInProgress(true);
      console.info('[auth] resend: starting verification resend flow', { email });
      const cred = await signInWithEmailAndPassword(auth, email, password);
      await cred.user.reload();

      if (!requiresVerifiedPasswordLogin(auth.currentUser)) {
        console.info('[auth] resend: email already verified', {
          uid: cred.user.uid,
          email,
        });
        persistVerificationEmailTarget('');
        setShowVerificationActions(true);
        await signOut(auth);
        setInfoMessage(t('emailAlreadyVerified'));
        return;
      }

      const sendResult = await sendVerificationEmailWithLogging(
        auth.currentUser ?? cred.user,
        email,
        'resend'
      );

      await signOut(auth);

      if (!sendResult.ok) {
        setError(sendResult.message);
        return;
      }

      setInfoMessage(t('verificationEmailResentTo', { email }));
      setShowVerificationActions(true);
      setResendCooldown(60);
    } catch (err: any) {
      console.error('[auth] resend: failed', {
        email,
        code: err?.code || null,
        message: err?.message || null,
      });

      const code = String(err?.code || '');
      if (code === 'auth/too-many-requests') {
        setError(t('verificationTooManyRequests'));
        setResendCooldown(60);
      } else if (code === 'auth/user-token-expired' || code === 'auth/requires-recent-login') {
        setError(t('verificationRequiresRecentLogin'));
      } else if (code === 'auth/network-request-failed') {
        setError(t('verificationNetworkFailed'));
      } else {
        setError(
          getFirebaseErrorDetails(
            err,
            t('verificationEmailResendFailed')
          )
        );
      }
    } finally {
      clearVerificationFlowInProgress();
      setIsResendingVerification(false);
    }
  };

  // ✅ REGISTER (Firebase Auth + Firestore)
  // ✅ REGISTER (Firebase Auth + Firestore)
  const handleRegister = async (e: React.FormEvent) => {
  e.preventDefault();
  setError("");
  setInfoMessage('');
  setRegSuccess(false);
  setIsLoading(true);

  try {
    // ---------- validações ----------
    if (!regName || !regPassword) {
      setError(t('registerRequiredFields'));
      setIsLoading(false);
      return;
    }

    if (regPassword !== regConfirmPassword) {
      setError(t('registerPasswordsMismatch'));
      setIsLoading(false);
      return;
    }

    const email = (regEmail || regUsername).trim();

    if (!email || !email.includes("@")) {
      setError(t('registerInvalidEmail'));
      setIsLoading(false);
      return;
    }

    if (isDisposableEmailDomain(email)) {
      setError(t('disposableEmailBlockedError'));
      setIsLoading(false);
      return;
    }

    if (!regGender) {
      setError(t('genderRequiredError'));
      setIsLoading(false);
      return;
    }

const shouldGoToPayment = postRegisterAction === "payment";
if (shouldGoToPayment) {
  localStorage.setItem("open_subscribe_after_login", "true");
}
    // ---------- cria usuário ----------
    setVerificationFlowInProgress(true);
    const cred = await createUserWithEmailAndPassword(auth, email, regPassword);
    const sendVerificationResult = await sendVerificationEmailWithLogging(
      auth.currentUser ?? cred.user,
      email,
      'register'
    );


    // ---------- grava doc do usuário ----------
    const plan = buildInactivePlan();

    const userDoc = {
      subscriptionTier: "free",
      subscriptionStatus: "free",
      ...buildPlanPatch(plan),
      limits: getPlanUsageLimits("free"),
      usage: buildInitialUsageState(),
      paymentDate: null,
      displayName: regName,

      // perfil
      name: regName,
      username: regUsername.trim(),
      email,
      crm: regCRM,
      rqe: regRQE,
      specialty: regSpecialty,
      subspecialty: regSubspecialty,
      gender: regGender,
      hospital: regHospital,
      phone: regPhone,
      professionalVerification: {
        status: "not_submitted",
        method: "none",
        crm: "",
        crmState: "",
        rqe: "",
        specialty: regSpecialty,
        subspecialty: regSubspecialty,
        message: "",
      },
      profile: {
        name: regName,
        crm: regCRM,
        rqe: regRQE,
        specialty: regSpecialty,
        subspecialty: regSubspecialty,
        gender: regGender,
        crmState: "",
        hospital: regHospital,
        phone: regPhone,
        email,
      },
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    await setDoc(doc(db, "users", cred.user.uid), userDoc, { merge: true });
    await signOut(auth);
    setUsername(email);
    if (!sendVerificationResult.ok) {
      setError(sendVerificationResult.message);
      setInfoMessage(t('verificationAccountCreatedResendPrompt', { email }));
    } else {
      setInfoMessage(t('verificationEmailSentTo', { email }));
    }
    setShowVerificationActions(true);
    setPendingUserUid(null);
    setPostRegisterAction(null);
    setRegSuccess(false);
    setCurrentView("login");
    setIsLoading(false);
    return;

    // ---------- pós cadastro ----------
    setIsLoading(false);

    // ✅ Limpamos a intenção para não ficar “presa”
    if (postRegisterAction === "payment") {
      setPostRegisterAction(null);
    }

if (shouldGoToPayment) {
   setPendingUserUid(cred.user.uid);
  setIsLoading(false);
  return;
}

setPendingUserUid(null);
setPostRegisterAction(null);
setRegSuccess(false);
setCurrentView("login");
return;
  } catch (err: any) {
    console.error(err);

    if (err?.code === "auth/email-already-in-use") {
      setError(t('registerEmailAlreadyUsed'));
      setCurrentView("login");
    } else if (err?.code === "auth/invalid-email") {
      setError(t('registerInvalidEmail'));
    } else if (err?.code === "auth/weak-password") {
      setError(t('registerWeakPassword'));
    } else {
      setError(t('registerCreateUserFailed'));
    }

    setIsLoading(false);
  } finally {
    clearVerificationFlowInProgress();
  }
};

  const handleCopyPix = () => {
    if (adminConfig?.pixKey) {
      navigator.clipboard.writeText(adminConfig.pixKey);
      setCopiedKey(true);
      setTimeout(() => setCopiedKey(false), 2000);
    }
  };

  const handleProcessPayment = async () => {
  console.log("Clique no pagar", { selectedPlan, paymentMethod, selectedPlanConfig });
// ✅ GARANTE que só abre Stripe se estiver logado
if (!auth.currentUser) {

  setError(t('loginCreateAccountBeforeSubscribe'));
  setCurrentView("register"); // manda para cadastro
  return;
}
  if (!onOpenSubscribe) {
    setError(t('paymentNotConfigured'));
    return;
  }

  try {
    setIsProcessingPayment(true);
    setError("");

    const successUrl = `${window.location.origin}/app?checkout=success`;
    const cancelUrl = `${window.location.origin}/app?checkout=cancel`;

    await onOpenSubscribe({
      tier: selectedPlan,
      interval: "monthly",
      method: paymentMethod,
      successUrl,
      cancelUrl,
    } as any);
  } catch (err: any) {
    console.error("Erro no pagamento:", err);
    setError(err?.message || t('paymentStartError'));
  } finally {
    setIsProcessingPayment(false);
  }
};

  // --- RENDER HELPERS ---

  const renderPaymentScreen = () => (
    <div className="animate-in slide-in-from-right-8 duration-300 space-y-6">
      {regSuccess ? (
        <div className="text-center py-12 space-y-6">
          <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto animate-bounce">
            <CheckCircle2 className="w-10 h-10 text-green-500" />
          </div>
          <div className="space-y-2">
            <h3 className="text-2xl font-bold text-white">{t('paymentConfirmedTitle')}</h3>
            <p className="text-slate-400">{t('paymentConfirmedSubtitle')}</p>
          </div>
          <p className="text-xs text-slate-500 uppercase tracking-widest animate-pulse">{t('loginRedirecting')}</p>
        </div>
      ) : (
        <>
          <div className="text-center space-y-2">
            <h3 className="text-xl font-bold text-white">{t('loginChoosePlanTitle')}</h3>
            <p className="text-sm text-slate-400">{t('loginChoosePlanSubtitle')}</p>
          </div>

          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-xl flex items-center justify-center gap-2 text-red-400 text-xs font-bold animate-in slide-in-from-top-2">
              <AlertCircle className="w-4 h-4 shrink-0" />
              {error}
            </div>
          )}

          {/* Plan Selection */}
          <div className="grid grid-cols-2 gap-2">
  <button
    onClick={() => setSelectedPlan('pro')}
    className={`p-2 rounded-xl border flex flex-col items-center gap-1 transition-all ${
      selectedPlan === 'pro'
        ? 'bg-blue-600/20 border-blue-500 text-white shadow-lg shadow-blue-900/20'
        : 'bg-slate-900/40 border-slate-700 text-slate-400 hover:border-slate-600'
    }`}
  >
    <span className="text-[10px] font-bold uppercase tracking-widest">{t('planProLabelShort')}</span>
    <span className="text-sm font-bold">{t('planProPrice')}</span>
  </button>

  <button
    onClick={() => setSelectedPlan('premium')}
    className={`p-2 rounded-xl border flex flex-col items-center gap-1 transition-all ${
      selectedPlan === 'premium'
        ? 'bg-indigo-600/20 border-indigo-500 text-white ring-1 ring-indigo-500/50'
        : 'bg-slate-900/40 border-slate-700 text-slate-400 hover:border-slate-600'
    }`}
  >
    <span className="text-[10px] font-bold uppercase tracking-widest">{t('planPremiumLabelShort')}</span>
    <span className="text-sm font-bold">{t('planPremiumPrice')}</span>
  </button>
</div>

          {/* Payment Method Tabs */}
          <div className="bg-slate-900/50 p-1 rounded-xl flex">
            <button
              onClick={() => setPaymentMethod('credit_card')}
              className={`flex-1 py-2 rounded-lg text-xs font-bold flex items-center justify-center gap-2 transition-all ${paymentMethod === 'credit_card' ? 'bg-slate-800 text-white shadow' : 'text-slate-500 hover:text-slate-300'}`}
            >
              <CreditCard className="w-3 h-3" /> {t('paymentCreditCard')}
            </button>
            <button
              onClick={() => setPaymentMethod('pix')}
              className={`flex-1 py-2 rounded-lg text-xs font-bold flex items-center justify-center gap-2 transition-all ${paymentMethod === 'pix' ? 'bg-slate-800 text-white shadow' : 'text-slate-500 hover:text-slate-300'}`}
            >
              <QrCode className="w-3 h-3" /> {t('paymentPix')}
            </button>
          </div>

          {/* Payment Details Area (mantida visualmente) */}
          <div className="bg-slate-800/30 border border-slate-700 rounded-xl p-4 space-y-4 relative overflow-hidden">
            {paymentMethod === 'credit_card' ? (
              <div className="space-y-3 animate-in fade-in">
                <input className="w-full bg-slate-900/80 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:border-blue-500 outline-none" placeholder={t('paymentCardNumberPlaceholder')} />
                <div className="grid grid-cols-2 gap-3">
                  <input className="w-full bg-slate-900/80 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:border-blue-500 outline-none" placeholder={t('paymentCardExpiryPlaceholder')} />
                  <input className="w-full bg-slate-900/80 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:border-blue-500 outline-none" placeholder={t('paymentCardCvcPlaceholder')} />
                </div>
                <input className="w-full bg-slate-900/80 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:border-blue-500 outline-none" placeholder={t('paymentCardNamePlaceholder')} />
                <div className="pt-2 flex justify-center">
                  <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-900 border border-slate-700/50">
                    <ShieldCheck className="w-3 h-3 text-emerald-500" />
                    <span className="text-[10px] text-slate-400 font-medium">
                      {t('paymentSecureGatewayLabel')}
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-4 space-y-3 animate-in fade-in">
                {adminConfig && (adminConfig.pixKey || adminConfig.pixQrCode) ? (
                  <>
                    {adminConfig.pixQrCode ? (
                      <div className="bg-white p-2 rounded-xl">
                        <img src={adminConfig.pixQrCode} alt="Pix QR" className="w-40 h-40 object-contain" />
                      </div>
                    ) : (
                      <div className="bg-white p-2 rounded-lg">
                        <QrCode className="w-24 h-24 text-black" />
                      </div>
                    )}

                    <div className="text-center">
                      <p className="text-xs text-slate-400 font-bold uppercase tracking-widest mb-1">{t('paymentTransferTo')}</p>
                      <p className="text-sm font-bold text-white">{adminConfig.pixBeneficiary || 'Admin'}</p>
                      {adminConfig.pixBank && <p className="text-xs text-slate-500">{adminConfig.pixBank}</p>}
                    </div>

                    <div className="w-full flex items-center gap-2 p-2 bg-slate-900 rounded-lg border border-slate-700 mt-2 relative">
                      <code className="text-xs text-slate-400 flex-1 truncate px-2 font-mono">
                        {adminConfig.pixKey || t('paymentPixKeyHidden')}
                      </code>
                      {adminConfig.pixKey && (
                        <button
                          onClick={handleCopyPix}
                          className="p-2 hover:bg-slate-800 rounded-md text-slate-300 transition-colors"
                          title={t('paymentCopy')}
                        >
                          {copiedKey ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                        </button>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="text-center text-slate-500 space-y-2">
                    <AlertCircle className="w-10 h-10 mx-auto opacity-50" />
                    <p className="text-xs">{t('paymentPixNotConfigured')}</p>
                    <p className="text-[10px] uppercase">{t('paymentUseCardOption')}</p>
                  </div>
                )}
              </div>
            )}
          </div>

  <button
  type="button"
  onClick={handleProcessPayment}
  disabled={isProcessingPayment}
  className="w-full flex items-center justify-center gap-2 py-4 bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded-xl transition-all shadow-lg shadow-emerald-900/20 disabled:opacity-70 disabled:cursor-not-allowed"
>
  <ShieldCheck className="w-5 h-5" />
  {paymentMethod === 'pix'
    ? t('loginContinueSubscriptionPix')
    : t('loginContinueSubscriptionPlan', { plan: selectedPlanConfig.label })
  }
</button>

          <button
            type="button"
            onClick={() => { setCurrentView('login'); setError(''); }}
            className="w-full py-2 text-slate-500 hover:text-slate-300 text-xs font-bold uppercase tracking-widest flex items-center justify-center gap-2 transition-colors"
          >
            {t('cancel')}
          </button>
        </>
      )}
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-b from-purple-900/20 via-cyan-900/20 to-emerald-900/20 rounded-full blur-[100px] animate-pulse" />
        <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(rgba(59, 130, 246, 0.1) 1px, transparent 1px)', backgroundSize: '30px 30px' }}></div>
      </div>

      <div className="relative z-10 w-full max-w-[440px] p-5 lg:max-w-[460px] lg:p-6">
        <div
          className="pointer-events-none absolute left-1/2 top-24 h-72 w-72 -translate-x-1/2 rounded-full blur-3xl z-0"
          style={{ background: 'radial-gradient(circle, rgba(56,189,248,0.16) 0%, rgba(99,102,241,0.12) 38%, rgba(15,23,42,0) 72%)' }}
        />
        <div className="glass relative z-10 rounded-3xl border border-slate-700 p-5 shadow-2xl backdrop-blur-xl animate-in fade-in zoom-in-95 duration-500 sm:p-6">
          {currentView === 'login' && (
            <div className="absolute right-4 top-4 z-20 sm:right-5 sm:top-5">
              <LanguageSelector className="scale-90 sm:scale-100" />
            </div>
          )}

          <div className="mb-6 text-center sm:mb-7">
            <div className="mb-3 flex justify-center">
              <div className="relative mb-2 flex h-[220px] w-[220px] shrink-0 items-center justify-center">
                <div className="absolute inset-0 bg-gradient-to-b from-purple-500/20 via-cyan-500/20 to-emerald-500/20 blur-[50px] rounded-full animate-pulse" />
                <div className="absolute inset-0 bg-blue-500/10 blur-[80px] rounded-full mix-blend-screen" />

                <img
                  src="/ortho-logo.png"
                  alt="OrthoPrecision"
                  className="w-full h-full object-contain relative z-10 drop-shadow-[0_0_30px_rgba(59,130,246,0.5)] transform hover:scale-105 transition-transform duration-700 ease-in-out"
                />
              </div>
            </div>
            <h1
              className="text-4xl font-semibold text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 via-sky-400 to-violet-400 tracking-[-0.02em] mb-2"
              style={{ textShadow: '0 0 18px rgba(56, 189, 248, 0.12)' }}
            >
              OrthoPrecision
            </h1>
            {currentView === 'login' ? (
              <div className="mx-auto max-w-md space-y-2.5 animate-in fade-in slide-in-from-bottom-2 delay-100">
                <h2 className="text-lg sm:text-xl font-semibold text-white leading-tight">
                  {t('loginHeroTitle')}
                </h2>
                <p className="text-sm text-blue-100/90 font-medium leading-relaxed">
                  {t('loginHeroSubtitle')}
                </p>
                <p className="mt-2 text-xs sm:text-sm text-slate-400 leading-relaxed">
                  {t('loginHeroTagline')}
                </p>
              </div>
            ) : (
              <p className="text-slate-400 text-sm uppercase tracking-widest font-semibold">
                {currentView === 'register'
                  ? (postRegisterAction === 'payment'
                      ? t('activatePlan')
                      : t('freeSignup'))
                  : (currentView === 'payment'
                      ? t('activateLicense')
                      : t('recoveryAccessTitle'))}
              </p>
              )}
            </div>

          {currentView === 'trial' ? (
            <div className="space-y-4 animate-in slide-in-from-right-8 duration-300">
              {/* Header trial */}
              <div className="rounded-xl border border-blue-500/25 bg-blue-500/8 px-4 py-4 text-center">
                <div className="text-2xl mb-2">🎁</div>
                <h3 className="text-base font-black text-white">{t('trialTitle')}</h3>
                <p className="text-xs text-blue-300/80 mt-1 leading-relaxed">
                  {t('trialSubtitle')}
                </p>
              </div>

              {infoMessage && (
                <div className="flex items-center gap-2 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-xl text-emerald-300 text-sm">
                  {infoMessage}
                </div>
              )}
              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-sm">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  {error}
                </div>
              )}

              {trialAuthMode === 'options' && (
                <>
                  {/* Google */}
                  <button
                    type="button"
                    onClick={handleGoogleTrialLogin}
                    disabled={isLoading}
                    className="w-full flex items-center justify-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-900 shadow-[0_10px_25px_rgba(15,23,42,0.12)] transition-all hover:bg-slate-50 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0" aria-hidden="true">
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                    </svg>
                    {isLoading ? <div className="w-5 h-5 border-2 border-slate-400/30 border-t-slate-700 rounded-full animate-spin" /> : t('trialContinueWithGoogle')}
                  </button>

                  {/* Apple */}
                  <button
                    type="button"
                    onClick={handleAppleTrialLogin}
                    disabled={isLoading}
                    className="w-full flex items-center justify-center gap-3 rounded-[14px] bg-black px-4 py-3 text-sm font-semibold text-white transition-all hover:bg-neutral-900 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <svg className="h-5 w-5 shrink-0" viewBox="0 0 170 170" aria-hidden="true" fill="white">
                      <path d="M150.37 130.25c-2.45 5.66-5.35 10.87-8.71 15.66-4.58 6.53-8.33 11.05-11.22 13.56-4.48 4.12-9.28 6.23-14.42 6.35-3.69 0-8.14-1.05-13.32-3.18-5.197-2.12-9.973-3.17-14.34-3.17-4.58 0-9.492 1.05-14.746 3.17-5.262 2.13-9.501 3.24-12.742 3.35-4.929.21-9.842-1.96-14.746-6.52-3.13-2.73-7.045-7.41-11.735-14.04-5.032-7.08-9.169-15.29-12.41-24.65-3.471-10.11-5.211-19.9-5.211-29.378 0-10.857 2.346-20.221 7.045-28.068 3.693-6.303 8.606-11.275 14.755-14.925s12.793-5.51 19.948-5.629c3.915 0 9.049 1.211 15.429 3.591 6.362 2.388 10.447 3.599 12.238 3.599 1.339 0 5.877-1.416 13.57-4.239 7.275-2.618 13.415-3.702 18.445-3.275 13.63 1.1 23.87 6.473 30.68 16.153-12.19 7.386-18.22 17.731-18.1 31.002.11 10.337 3.86 18.939 11.23 25.769 3.34 3.17 7.07 5.62 11.22 7.36-.9 2.61-1.85 5.11-2.86 7.51zM119.11 7.24c0 8.102-2.96 15.667-8.86 22.669-7.12 8.324-15.732 13.134-25.071 12.375a25.222 25.222 0 0 1-.188-3.07c0-7.778 3.386-16.102 9.399-22.908 3.002-3.446 6.82-6.311 11.45-8.597 4.62-2.252 8.99-3.497 13.1-3.71.12 1.083.17 2.166.17 3.241z"/>
                    </svg>
                    Sign in with Apple
                  </button>

                  <div className="flex items-center gap-3 py-1">
                    <div className="h-px flex-1 bg-slate-700/50" />
                    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{t('trialOrDivider')}</span>
                    <div className="h-px flex-1 bg-slate-700/50" />
                  </div>

                  {/* Email/senha */}
                  <button
                    type="button"
                    onClick={() => { setTrialAuthMode('signin'); setError(''); setInfoMessage(''); }}
                    disabled={isLoading}
                    className="w-full flex items-center justify-center gap-2.5 rounded-xl border border-slate-700 bg-slate-900/60 px-4 py-3 text-sm font-semibold text-slate-200 transition-all hover:bg-slate-800 hover:border-slate-600 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <Mail className="w-4 h-4" /> {t('trialAlreadyHaveAccountOption')}
                  </button>

                  <button
                    type="button"
                    onClick={() => { setTrialAuthMode('signup'); setError(''); setInfoMessage(''); }}
                    disabled={isLoading}
                    className="w-full flex items-center justify-center gap-2.5 rounded-xl border border-blue-500/30 bg-blue-500/10 px-4 py-3 text-sm font-bold text-blue-200 transition-all hover:bg-blue-500/15 hover:border-blue-500/50 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <UserPlus className="w-4 h-4" /> {t('trialCreateAccountOption')}
                  </button>

                  <button
                    type="button"
                    onClick={() => { setCurrentView('login'); resetTrialAuthForm(); }}
                    className="w-full py-2.5 text-xs font-bold uppercase tracking-widest text-slate-400 hover:text-white transition-colors flex items-center justify-center gap-2"
                  >
                    <ArrowLeft className="w-3 h-3" /> {t('trialBackToLogin')}
                  </button>
                </>
              )}

              {(trialAuthMode === 'signin' || trialAuthMode === 'signup') && (
                <form
                  onSubmit={trialAuthMode === 'signin' ? handleEmailTrialSignIn : handleEmailTrialSignUp}
                  className="space-y-3"
                >
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <Mail className="h-5 w-5 text-slate-500 group-focus-within:text-blue-400 transition-colors" />
                    </div>
                    <input
                      type="email"
                      value={trialEmail}
                      onChange={(e) => setTrialEmail(e.target.value)}
                      className="block w-full pl-12 pr-4 py-3 bg-slate-900/60 border border-slate-700 rounded-xl text-slate-200 placeholder-slate-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all"
                      placeholder={t('trialEmailPlaceholder')}
                      autoComplete="email"
                    />
                  </div>

                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <Lock className="h-5 w-5 text-slate-500 group-focus-within:text-blue-400 transition-colors" />
                    </div>
                    <input
                      type="password"
                      value={trialPassword}
                      onChange={(e) => setTrialPassword(e.target.value)}
                      className="block w-full pl-12 pr-4 py-3 bg-slate-900/60 border border-slate-700 rounded-xl text-slate-200 placeholder-slate-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all"
                      placeholder={t('passwordRequiredPlaceholder')}
                      autoComplete={trialAuthMode === 'signin' ? 'current-password' : 'new-password'}
                    />
                  </div>

                  {trialAuthMode === 'signup' && (
                    <div className="relative group">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <Lock className="h-5 w-5 text-slate-500 group-focus-within:text-blue-400 transition-colors" />
                      </div>
                      <input
                        type="password"
                        value={trialConfirmPassword}
                        onChange={(e) => setTrialConfirmPassword(e.target.value)}
                        className="block w-full pl-12 pr-4 py-3 bg-slate-900/60 border border-slate-700 rounded-xl text-slate-200 placeholder-slate-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all"
                        placeholder={t('confirmRequiredPlaceholder')}
                        autoComplete="new-password"
                      />
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full flex items-center justify-center gap-2 py-3 bg-linear-to-r from-cyan-600 to-purple-600 hover:from-cyan-500 hover:to-purple-500 text-white font-bold rounded-xl transition-all shadow-lg shadow-purple-900/20 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? (
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <>
                        <span className="text-base">🎁</span>
                        {trialAuthMode === 'signin' ? t('trialSignInSubmit') : t('trialSignUpSubmit')}
                      </>
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={() => { setTrialAuthMode('options'); setError(''); setInfoMessage(''); }}
                    className="w-full py-2.5 text-xs font-bold uppercase tracking-widest text-slate-400 hover:text-white transition-colors flex items-center justify-center gap-2"
                  >
                    <ArrowLeft className="w-3 h-3" /> {t('trialBackToOptions')}
                  </button>
                </form>
              )}
            </div>
          ) : currentView === 'payment' && postRegisterAction === "payment" ? (
  renderPaymentScreen()
) : currentView === 'forgot_password' ? (
            <div className="space-y-6 animate-in slide-in-from-right-8 duration-300">
              {resetSuccess ? (
                <div className="text-center py-8 space-y-6">
                  <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto border border-blue-500/30">
                    <Mail className="w-8 h-8 text-blue-400" />
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-xl font-bold text-white">{t('verifyEmail')}</h3>
                    <p className="text-slate-400 text-sm">{t('passwordResetSentTo', { email: resetEmail })}</p>
                  </div>
                  <button
                    onClick={() => { setCurrentView('login'); setResetSuccess(false); setResetEmail(''); }}
                    className="w-full py-3 bg-slate-800 hover:bg-slate-700 text-white rounded-xl text-xs font-bold uppercase tracking-widest transition-all border border-slate-700"
                  >
                    {t('backToLogin')}
                  </button>
                </div>
              ) : (
                <form onSubmit={handleResetRequest} className="space-y-6">
                  <div className="text-center space-y-2">
                    <p className="text-sm text-slate-400 leading-relaxed">
                      {t('recoveryInstructions')}
                    </p>
                  </div>

                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <Mail className="h-5 w-5 text-slate-500 group-focus-within:text-blue-400 transition-colors" />
                    </div>
                    <input
                      type="email"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      className="block w-full pl-12 pr-4 py-4 bg-slate-900/60 border border-slate-700 rounded-xl text-slate-200 placeholder-slate-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all"
                      placeholder={t('registeredEmailPlaceholder')}
                      required
                    />
                  </div>

                  {error && (
                    <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-sm">
                      <AlertCircle className="w-4 h-4 shrink-0" />
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full flex items-center justify-center gap-2 py-4 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-xl transition-all shadow-lg shadow-blue-900/20 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? (
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <>
                        <Send className="w-4 h-4" /> {t('sendRecoveryLink')}
                      </>
                    )}
                  </button>

                  <button
  type="button"
  onClick={() => {
    setCurrentView('login');
    setError('');
    setPostRegisterAction(null);
    setPendingUserUid(null);
  }}
  className="w-full py-2 text-slate-500 hover:text-slate-300 text-xs font-bold uppercase tracking-widest flex items-center justify-center gap-2 transition-colors"
>
                    <ArrowLeft className="w-3 h-3" /> {t('cancel')}
                  </button>
                </form>
              )}
            </div>
          ) : currentView === 'register' ? (
            <div className="space-y-4 animate-in slide-in-from-right-8 duration-300 max-h-[60vh] overflow-y-auto custom-scrollbar pr-2">
              {regSuccess ? (
                <div className="text-center py-12 space-y-6">
                  <div className="w-20 h-20 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto animate-bounce">
                    <Sparkles className="w-10 h-10 text-blue-500" />
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-2xl font-bold text-white">{t('freeSignup')}</h3>
                    <p className="text-slate-400">{t('enjoyFreePlan')}</p>
                  </div>
                  <p className="text-xs text-slate-500 uppercase tracking-widest animate-pulse">{t('entering')}</p>
                </div>
              ) : (
                <form onSubmit={handleRegister} className="space-y-6">
                  {/* Account Data */}
                  <div className="space-y-3">
                    <h4 className="text-xs text-blue-400 font-bold uppercase tracking-widest mb-1 flex items-center gap-1"><User className="w-3 h-3" /> {t('accessLabel')}</h4>
                    <input
                      type="text"
                      value={regName}
                      onChange={(e) => setRegName(e.target.value)}
                      className="block w-full px-4 py-3 bg-slate-900/60 border border-slate-700 rounded-xl text-slate-200 placeholder-slate-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all text-sm"
                      placeholder={t('fullNameRequiredPlaceholder')}
                    />

                    {/* Mantive seu ID, mas no Firebase o que importa é o Email */}
                    <input
                      type="text"
                      value={regUsername}
                      onChange={(e) => setRegUsername(e.target.value)}
                      className="block w-full px-4 py-3 bg-slate-900/60 border border-slate-700 rounded-xl text-slate-200 placeholder-slate-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all text-sm"
                      placeholder={t('userIdOptionalPlaceholder')}
                      autoComplete="off"
                    />

                    <div className="grid grid-cols-2 gap-3">
                      <input
                        type="password"
                        value={regPassword}
                        onChange={(e) => setRegPassword(e.target.value)}
                        className="block w-full px-4 py-3 bg-slate-900/60 border border-slate-700 rounded-xl text-slate-200 placeholder-slate-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all text-sm"
                        placeholder={t('passwordRequiredPlaceholder')}
                      />
                      <input
                        type="password"
                        value={regConfirmPassword}
                        onChange={(e) => setRegConfirmPassword(e.target.value)}
                        className="block w-full px-4 py-3 bg-slate-900/60 border border-slate-700 rounded-xl text-slate-200 placeholder-slate-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all text-sm"
                        placeholder={t('confirmRequiredPlaceholder')}
                      />
                    </div>
                  </div>

                  {/* Ortho ID Data */}
                  <div className="space-y-3 pt-2 border-t border-slate-800">
                    <h4 className="text-xs text-emerald-400 font-bold uppercase tracking-widest mb-1 flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> {t('professionalLabel')}</h4>
                    <div className="grid grid-cols-2 gap-3">
                      <input
                        type="text"
                        value={regCRM}
                        onChange={(e) => setRegCRM(e.target.value)}
                        className="block w-full px-4 py-3 bg-slate-900/60 border border-slate-700 rounded-xl text-slate-200 placeholder-slate-500 focus:outline-none focus:border-emerald-500 transition-all text-sm"
                        placeholder={t('crmStatePlaceholder')}
                      />
                      <input
                        type="text"
                        value={regRQE}
                        onChange={(e) => setRegRQE(e.target.value)}
                        className="block w-full px-4 py-3 bg-slate-900/60 border border-slate-700 rounded-xl text-slate-200 placeholder-slate-500 focus:outline-none focus:border-emerald-500 transition-all text-sm"
                        placeholder={t('rqeOptionalPlaceholder')}
                      />
                    </div>
                    <div className="grid grid-cols-1 gap-3">
                      <div className="space-y-2">
                        <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{t('specialtyLabel')}</p>
                        <select
                          value={regSpecialty}
                          onChange={(e) => setRegSpecialty(e.target.value)}
                          className="block w-full px-4 py-3 bg-slate-900/60 border border-slate-700 rounded-xl text-slate-200 focus:outline-none focus:border-emerald-500 transition-all text-sm"
                        >
                          {specialtyOptions.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="space-y-2">
                        <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{t('subspecialtyLabel')}</p>
                        <input
                          type="text"
                          list="register-subspecialties"
                          value={regSubspecialty}
                          onChange={(e) => setRegSubspecialty(e.target.value)}
                          className="block w-full px-4 py-3 bg-slate-900/60 border border-slate-700 rounded-xl text-slate-200 placeholder-slate-500 focus:outline-none focus:border-emerald-500 transition-all text-sm"
                          placeholder={t('subspecialtyOptionalPlaceholder')}
                        />
                        <datalist id="register-subspecialties">
                          {subspecialtyOptions.map((option) => (
                            <option key={option} value={option} />
                          ))}
                        </datalist>
                      </div>
                    </div>
                    <select
                      value={regGender}
                      onChange={(e) => setRegGender(e.target.value)}
                      className="block w-full px-4 py-3 bg-slate-900/60 border border-slate-700 rounded-xl text-slate-200 focus:outline-none focus:border-emerald-500 transition-all text-sm"
                      required
                    >
                      <option value="">{t('genderRequiredPlaceholder')}</option>
                      <option value="male">{t('genderMale')}</option>
                      <option value="female">{t('genderFemale')}</option>
                    </select>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-3.5 w-4 h-4 text-slate-500" />
                      <input
                        type="text"
                        value={regHospital}
                        onChange={(e) => setRegHospital(e.target.value)}
                        className="block w-full pl-10 pr-4 py-3 bg-slate-900/60 border border-slate-700 rounded-xl text-slate-200 placeholder-slate-500 focus:outline-none focus:border-emerald-500 transition-all text-sm"
                        placeholder={t('mainHospitalPlaceholder')}
                      />
                    </div>
                  </div>

                  {/* Contact Data */}
                  <div className="space-y-3 pt-2 border-t border-slate-800">
                    <h4 className="text-xs text-amber-400 font-bold uppercase tracking-widest mb-1 flex items-center gap-1"><Smartphone className="w-3 h-3" /> {t('contactLabel')}</h4>
                    <div className="grid grid-cols-2 gap-3">
                      <input
                        type="text"
                        value={regPhone}
                        onChange={(e) => setRegPhone(e.target.value)}
                        className="block w-full px-4 py-3 bg-slate-900/60 border border-slate-700 rounded-xl text-slate-200 placeholder-slate-500 focus:outline-none focus:border-amber-500 transition-all text-sm"
                        placeholder={t('phoneCellPlaceholder')}
                      />
                      <input
                        type="email"
                        value={regEmail}
                        onChange={(e) => setRegEmail(e.target.value)}
                        className="block w-full px-4 py-3 bg-slate-900/60 border border-slate-700 rounded-xl text-slate-200 placeholder-slate-500 focus:outline-none focus:border-amber-500 transition-all text-sm"
                        placeholder={t('emailLoginPlaceholder')}
                      />
                    </div>
                  </div>

                  {error && (
                    <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-xs font-bold">
                      <AlertCircle className="w-4 h-4 shrink-0" />
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full flex items-center justify-center gap-2 py-4 text-white font-bold rounded-xl transition-all shadow-lg disabled:opacity-50 bg-emerald-600 hover:bg-emerald-500 shadow-emerald-900/20"
                  >
                    {isLoading ? (
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <>
                        <UserPlus className="w-5 h-5" />
                        {postRegisterAction === 'payment'
                          ? t('professionalPlanCta')
                          : t('createFreeAccount')}
                      </>
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={() => { setCurrentView('login'); setError(''); }}
                    className="w-full py-3 text-slate-400 hover:text-white text-xs font-bold uppercase tracking-widest flex items-center justify-center gap-2 transition-colors"
                  >
                    <ArrowLeft className="w-3 h-3" /> {t('backToLogin')}
                  </button>
                </form>
              )}
            </div>
          ) : (
            // LOGIN FORM
            <div className="space-y-4 animate-in slide-in-from-left-8 duration-300">
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-3">
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <User className="h-5 w-5 text-slate-500 group-focus-within:text-blue-400 transition-colors" />
                    </div>
                    <input
                      type="text"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      className="block w-full pl-12 pr-4 py-3 bg-slate-900/60 border border-slate-700 rounded-xl text-slate-200 placeholder-slate-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all"
                      placeholder={t('surgeonIdEmailPlaceholder')}
                      autoComplete="username"
                    />
                  </div>

                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <Lock className="h-5 w-5 text-slate-500 group-focus-within:text-blue-400 transition-colors" />
                    </div>
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="block w-full pl-12 pr-4 py-3 bg-slate-900/60 border border-slate-700 rounded-xl text-slate-200 placeholder-slate-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all"
                      placeholder={t('accessPasswordPlaceholder')}
                      autoComplete="current-password"
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setCurrentView('forgot_password')}
                    className="text-xs text-blue-400 hover:text-blue-300 font-medium flex items-center gap-1 transition-colors"
                  >
                    <KeyRound className="w-3 h-3" /> {t('forgotPassword')}
                  </button>
                </div>

                {infoMessage && (
                  <div className="flex items-center gap-2 p-3 bg-blue-500/10 border border-blue-500/20 rounded-xl text-blue-300 text-sm animate-in slide-in-from-top-2">
                    <Info className="w-4 h-4 shrink-0" />
                    {infoMessage}
                  </div>
                )}

                {error && (
                  <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-sm animate-in slide-in-from-top-2">
                    <AlertCircle className="w-4 h-4 shrink-0" />
                    {error}
                  </div>
                )}

                {/* Botão Trial */}
                <button
                  type="button"
                  onClick={() => { setCurrentView('trial'); resetTrialAuthForm(); }}
                  disabled={isLoading}
                  className="w-full flex items-center justify-center gap-2.5 rounded-xl border border-blue-500/30 bg-blue-500/10 px-4 py-3 text-sm font-bold text-blue-200 transition-all hover:bg-blue-500/15 hover:border-blue-500/50 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <span className="text-base">🎁</span>
                  {t('startFreeTrialCta')}
                </button>

                <button
                  type="button"
                  onClick={handleGoogleLogin}
                  disabled={isLoading}
                  className="w-full flex items-center justify-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-900 shadow-[0_10px_25px_rgba(15,23,42,0.12)] transition-all hover:bg-slate-50 hover:border-slate-300 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0" aria-hidden="true">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                  {t('signInWithGoogle')}
                </button>

                {/* Apple Sign-In Web */}
                <button
                  type="button"
                  onClick={handleAppleLogin}
                  disabled={isLoading}
                  className="w-full flex items-center justify-center gap-3 rounded-[14px] bg-black px-4 py-3 text-sm font-semibold text-white transition-all hover:bg-neutral-900 active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <svg className="h-5 w-5 shrink-0" viewBox="0 0 170 170" aria-hidden="true" fill="white">
                    <path d="M150.37 130.25c-2.45 5.66-5.35 10.87-8.71 15.66-4.58 6.53-8.33 11.05-11.22 13.56-4.48 4.12-9.28 6.23-14.42 6.35-3.69 0-8.14-1.05-13.32-3.18-5.197-2.12-9.973-3.17-14.34-3.17-4.58 0-9.492 1.05-14.746 3.17-5.262 2.13-9.501 3.24-12.742 3.35-4.929.21-9.842-1.96-14.746-6.52-3.13-2.73-7.045-7.41-11.735-14.04-5.032-7.08-9.169-15.29-12.41-24.65-3.471-10.11-5.211-19.9-5.211-29.378 0-10.857 2.346-20.221 7.045-28.068 3.693-6.303 8.606-11.275 14.755-14.925s12.793-5.51 19.948-5.629c3.915 0 9.049 1.211 15.429 3.591 6.362 2.388 10.447 3.599 12.238 3.599 1.339 0 5.877-1.416 13.57-4.239 7.275-2.618 13.415-3.702 18.445-3.275 13.63 1.1 23.87 6.473 30.68 16.153-12.19 7.386-18.22 17.731-18.1 31.002.11 10.337 3.86 18.939 11.23 25.769 3.34 3.17 7.07 5.62 11.22 7.36-.9 2.61-1.85 5.11-2.86 7.51zM119.11 7.24c0 8.102-2.96 15.667-8.86 22.669-7.12 8.324-15.732 13.134-25.071 12.375a25.222 25.222 0 0 1-.188-3.07c0-7.778 3.386-16.102 9.399-22.908 3.002-3.446 6.82-6.311 11.45-8.597 4.62-2.252 8.99-3.497 13.1-3.71.12 1.083.17 2.166.17 3.241z"/>
                  </svg>
                  Sign in with Apple
                </button>

                {showVerificationActions && verificationEmailTarget && (
                  <>
                    <p className="text-center text-xs text-slate-400">{t('verificationSpamHint')}</p>
                    <button
                      type="button"
                      onClick={handleResendVerificationEmail}
                      disabled={isLoading || isResendingVerification || resendCooldown > 0}
                      className="w-full flex items-center justify-center gap-2 py-3 border border-cyan-500/20 bg-cyan-500/10 hover:bg-cyan-500/15 text-cyan-100 font-bold rounded-xl transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {isResendingVerification ? (
                        <div className="w-5 h-5 border-2 border-cyan-100/30 border-t-cyan-100 rounded-full animate-spin" />
                      ) : (
                        <>
                          <Send className="w-4 h-4" />
                          {resendCooldown > 0
                            ? t('resendVerificationEmailCountdown', { seconds: resendCooldown })
                            : t('resendVerificationEmail')}
                        </>
                      )}
                    </button>
                  </>
                )}

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full flex items-center justify-center gap-2 py-3 bg-linear-to-r from-cyan-600 to-purple-600 hover:from-cyan-500 hover:to-purple-500 text-white font-bold rounded-xl transition-all shadow-lg shadow-purple-900/20 disabled:opacity-50 disabled:cursor-not-allowed group"
                >
                  {isLoading ? (
                    <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      {t('accessSystem')} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </>
                  )}
                </button>
              </form>

              <div className="flex flex-col gap-2.5 border-t border-slate-700/50 pt-4">
                <p className="text-center text-[10px] text-slate-500 font-bold uppercase tracking-widest mb-1">
  {t('chooseHowToStart')}
</p>
                <button
  type="button"
  onClick={startRegistration}
  className="w-full py-3 bg-slate-800 hover:bg-slate-700 hover:text-white border border-slate-700 hover:border-cyan-500/50 rounded-xl text-slate-200 text-xs font-bold uppercase tracking-widest transition-all flex items-center justify-center gap-2 group"
>
  <UserPlus className="w-4 h-4 group-hover:scale-110 transition-transform" />
  {t('createFreeAccount')}
</button>

<button
  type="button"
  onClick={() => {
    setPostRegisterAction("payment");
    setCurrentView("register");
    setError("");
  }}
  className="w-full py-3 bg-emerald-900/20 hover:bg-emerald-900/30 border border-emerald-500/30 hover:border-emerald-500/60 text-emerald-400 hover:text-emerald-300 rounded-xl text-xs font-bold uppercase tracking-widest transition-all flex items-center justify-center gap-2 group shadow-lg shadow-emerald-900/10"
>
  <CreditCard className="w-4 h-4" />
  {t('professionalPlanCta')}
</button>
              </div>
            </div>
          )}

          {currentView === 'login' && (
            <div className="mt-5 flex items-center justify-between border-t border-slate-700/50 pt-4 text-xs text-slate-500">
              <div className="flex items-center gap-1">
                <Fingerprint className="w-3 h-3" /> {t('loginSecureSsl')}
              </div>
              <span className="opacity-50">v2.5.0</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginScreen;
